<template>
  <Dialog
    width="860px"
    :height="createRenderType == 'mobile' ? '100%' : '720px'"
    titleAlign="left"
    :title="$$t('editor.cover_voice')"
    v-model:visible="renderData.visible"
    @close="closeDialog"
    :background="
      createRenderType == 'mobile' && !props.item?.song_id
        ? 'linear-gradient(180deg, rgba(250, 165, 255, 0.3) 0%, rgba(250, 165, 255, 0) 100%), black'
        : null
    "
    mobileRenderType="drawerDialog"
    :customClass="`cover-voice-dialog ${
      createRenderType == 'mobile' ? 'cover-voice-dialog--mobile' : ''
    }`"
  >
    <div
      class="cover-voice-dialog-container"
      :class="{
        [`cover-voice-dialog-container--${createRenderType}`]: true,
      }"
    >
      <div class="cover-voice-dialog-edit-box">
        <div class="cover-voice-dialog-media-box">
          <div
            class="cover-voice-dialog-media-upload"
            v-if="renderData.editMode == 'upload'"
          >
            <AudioDragUpload
              pointTypePrefix="cover_voice"
              :uploadDescription="$$t('editor.cover_voice_edit_description')"
              @initEditData="doInitEditData"
              @syncUploadState="doSyncUploadState"
            ></AudioDragUpload>
          </div>
          <div
            class="cover-voice-dialog-media-processing"
            v-if="renderData.editMode == 'processing'"
          >
            <div class="redm-processing-loading-box">
              <div class="redm-processing-loading rotate-loading"></div>
              <div class="redm-processing-loading-ico flex-center">
                <i class="icon"> </i>
              </div>
            </div>
            <div class="cover-voice-dialog-media-processing-text">
              {{ $$t("editor.audio_edit_data_processing") }}
            </div>
          </div>
          <div
            class="cover-voice-dialog-media-processfailed"
            v-if="renderData.editMode == 'processfailed'"
          >
            <div class="region-processing-processfailed-ico"></div>
            <div class="cover-voice-dialog-media-processfailed-text tc">
              {{ $$t("editor.get_editing_data_failed") }}
            </div>
            <div class="cover-voice-dialog-media-processfailed-button">
              <div
                class="cover-voice-dialog-media-processfailed-button-item"
                v-if="!props.item?.song_id"
              >
                <Button
                  @click="onCancelClick"
                  :renderStyle="{
                    '--padding': '0',
                    '--width': '100%',
                    '--height': '32px',
                    '--borderRadius': '48px',
                    '--background': 'rgba(255, 255, 255, 0.2)',
                    '--fontColor': '#fff',
                    '--fontSize': '14px',
                    '--fontWeight': 'normal',
                    '--border': 'none',
                    '--backgroundHover': 'rgba(255, 255, 255, 0.3)',
                    '--fontColorHover': '#fff',
                  }"
                >
                  <span class="fn-inline">{{ $$t("create.cancel") }}</span>
                </Button>
              </div>
              <div class="cover-voice-dialog-media-processfailed-button-item">
                <Button
                  @click="onRetryClick"
                  :renderStyle="{
                    '--padding': '0',
                    '--width': '100%',
                    '--height': '32px',
                    '--borderRadius': '48px',
                    '--background': 'rgba(255, 255, 255, 0.2)',
                    '--fontColor': '#fff',
                    '--fontSize': '14px',
                    '--fontWeight': 'normal',
                    '--border': 'none',
                    '--backgroundHover': 'rgba(255, 255, 255, 0.3)',
                    '--fontColorHover': '#fff',
                  }"
                >
                  <span class="fn-inline">{{ $$t("create.retry") }}</span>
                </Button>
              </div>
            </div>
          </div>

          <div
            class="cover-voice-dialog-media-edit"
            v-if="renderData.editMode == 'editing'"
          >
            <EditorWavePlayer
              :canSeekNotCurrent="true"
              :autoToStartIfNotCurrent="false"
              :url="renderData.audioUrl"
              :duration="renderData.audioDuration"
              :waveformData="renderData.waveformData"
              :hideRemove="renderData.hideRemove"
              @remove="doRemove"
            ></EditorWavePlayer>
          </div>
        </div>
        <div class="cover-voice-dialog-lyrics-box">
          <CoverLibrary></CoverLibrary>
        </div>
      </div>
      <div class="cover-voice-dialog-button-box">
        <CreditsButton
          :buttonText="$$t('editor.cover_voice')"
          :disabled="disabledSubmit"
          :loading="renderData.loadingSubmit"
          @onClick="onSubmitClick"
          :membership_source="renderData.membership_source"
        ></CreditsButton>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  getCurrentInstance,
} from "vue"

import { useRouter, useRoute } from "vue-router"
import globalConfig, { chinaAppName } from "@/config/config.js"

import apiEditor from "@/api/api/apiEditor.js"
import apiCommon from "@/api/api/apiCommon.js"

import { showActionDialog } from "@/components/functionCallComponent/action/action.js"

import AudioDragUpload from "../component/audioDragUpload.vue"
import CoverLibrary from "./coverLibrary.vue"

import CreditsButton from "../component/creditsButton.vue"
import AddLinkDialog from "../component/addLinkDialog.vue"
import Button from "@/components/basic/button/Button.vue"
import EditorWavePlayer from "../component/editorWavePlayer.vue"

import tools from "@/utils/tools.js"
import ffmpegUtil from "@/components/media/utils/ffmpegUtil.js"
import message from "@/components/functionCallComponent/message.jsx"
import clipUtil from "@/components/media/utils/clipUtil.js"
import objectStorage, {
  getObjectStorageFullUrl,
} from "@/lib/objectStorage/objectStorage.js"

import { storeToRefs } from "pinia"
import useCreateLayoutStore from "@/store/create/createLayoutStore.js"
import useUserDataStore from "@/store/userDataStore.js"
import useFeedListStore from "@/store/create/feedListDataStore.js"
import useCoverVoiceDataStore from "@/store/editor/coverVoiceDataStore.js"

const coverVoiceDataStore = useCoverVoiceDataStore()
const createLayoutStore = useCreateLayoutStore()
const userDataStore = useUserDataStore()
const { createRenderType } = storeToRefs(createLayoutStore)
const feedListDataStore = useFeedListStore()

const Router = useRouter()
const route = useRoute()
const { $$language, $$t, $$tm, $$tValue } = getCurrentInstance().appContext?.config.globalProperties.$i18n ?? {}

const globalProperties =
  getCurrentInstance()?.appContext.config.globalProperties

const props = defineProps({
  pitem: {
    type: Object,
    default: null,
  },
  item: {
    type: Object,
    default: null,
  },
  visible: {
    type: Boolean,
    default: false,
  },
})

const refToggleEditLyrics = ref(null)
const emits = defineEmits(["update:visible"])

const stateData = {
  parseDataParams: null,
  lyricUnitTimeDataArray: [],
}

const renderData = reactive({
  visible: props.visible,
  editMode: "upload", // "upload", "processing","editing", "processfailed"
  addLinkDialogVisible: false,
  hideRemove: props.item?.song_id ? true : false,
  linkParseUrl: "",
  file: null,
  linkUrl: "",
  clipSourceData: null,
  editFileName: "",
  audioUrl: "", // https://test.mureka.ai/cos-test/song/audio/20250213/music_56098979577857_PfJV1K6VMYcGNNWnzYropf_bcfiec.mp3",
  audioDuration: 160.183,
  waveformData: Array.from(
    {
      length: 1000,
    },
    (v, i) => Math.random()
  ),
  loadingSubmit: false,
  membership_source: {
    membership_source1: props.item?.song_id ? "membership_feature" : "editor",
    membership_source2: props.item?.song_id ? "regenerate" : "cover_voice",
  },
})

const disabledSubmit = computed(() => {
  return (
    renderData.editMode !== "editing" || !coverVoiceDataStore.curUseReferMusic
  )
})

const doDownloadFile = async (url, filename) => {
  return new Promise((resolve, reject) => {
    var xhr = new XMLHttpRequest()
    xhr.open("GET", url, true)
    xhr.responseType = "blob"
    xhr.onload = function (e) {
      if (this.status === 200) {
        var blob = new Blob([this.response], {
          type: this.response?.type || "text/plain",
        })
        const resfile = new File([blob], filename, { type: blob.type })
        resolve(resfile)
      } else {
        reject(this.statusText)
      }
    }
    xhr.send()
  })
}

const closeDialog = () => {
  if (renderData.editMode === "editing") {
    showActionDialog({
      content: $$t("editor.edit_close_warning"),
      cancelLabel: $$t("editor.keep"),
      confirmLabel: $$t("create.close"),
      titleAlign: "center",
      confirm: (closeAction) => {
        closeAction()
        renderData.visible = false
      },
    })
  } else {
    renderData.visible = false
  }
}

const onQuestionClick = () => {
  showActionDialog({
    customClass: "question-help-dialog",
    title: $$t("editor.note_on_using_region_edit"),
    content: $$t("editor.cover_voice_subtitle"),
    hideCancel: true,
    confirmLabel: "ok",
    titleAlign: "left",
    confirm: (closeAction) => {
      closeAction()
    },
  })
}

const doRemove = async () => {
  showActionDialog({
    content: $$t("editor.edit_remove_warning"),
    cancelLabel: $$t("editor.keep"),
    confirmLabel: $$t("create.delete"),
    titleAlign: "center",
    confirm: (closeAction) => {
      closeAction()
      renderData.editMode = "upload"
      renderData.from = 0
      renderData.to = 0
      stateData.lyricUnitTimeDataArray = []
    },
  })
}

const doSyncUploadState = () => {}

const doInitEditData = async (data) => {
  renderData.editMode = "processing"
  doParseInitEditData(data)
}

const doParseInitEditData = (data) => {
  renderData.editMode = "processing"
  stateData.parseDataParams = {
    resource: data.linkUrl,
    source_type: data.source_type,
    cos_url: data?.cos_url,
    duration_milliseconds: data?.duration_milliseconds,
    title: data?.title,
    is_need_waves: true,
    is_need_lyrics: true,
    check_mute_duration: true,
  }
  return new Promise((resolve, reject) => {
    apiEditor
      .parseAudioData(stateData.parseDataParams)
      .then((res) => {
        if (res.code == 200) {
          renderData.waveformData = res.data.waves
          renderData.audioDuration = res.data.duration_milliseconds / 1000
          renderData.audioUrl =
            getObjectStorageFullUrl(res.data.mp3_url) + "?type=extendEditing"
          renderData.title = res.data.title
          renderData.editMode = "editing"
          stateData.parseResData = res.data
        } else {
          if (res.code == 6346) {
            message.error({
              content: $$t("editor.check_mute_duration_warning"),
              positon: "top",
            })
            onCancelClick()
          } else if (res.code == 6316) {
            message.error({
              content: $$t("create.song_state_auditing_failed"),
              positon: "top",
            })
            onCancelClick()
          } else {
            message.error({
              content: $$t("editor.get_editing_data_failed_message"),
              positon: "top",
            })
            renderData.editMode = "processfailed"
          }
        }
      })
      .catch((err) => {
        message.error({
          content: $$t("editor.get_editing_data_failed_message"),
          positon: "top",
        })
        renderData.editMode = "processfailed"
        reject(err)
      })
  })
}

const doParseInitEditDataWithSong = () => {
  renderData.editMode = "processing"
  return new Promise((resolve, reject) => {
    apiCommon
      .getSongInfo({ song_id: props.item.song_id, check_mute_duration: true })
      .then((res) => {
        if (res.code == 200) {
          renderData.waveformData = res.data.waves
          renderData.audioDuration = res.data.duration_milliseconds / 1000
          renderData.audioUrl =
            getObjectStorageFullUrl(res.data.mp3_url) + "?type=extendEditing"
          renderData.title = res.data.title
          renderData.editMode = "editing"
          stateData.parseResData = res.data
        } else {
          if (res.code == 6346) {
            message.error({
              content: $$t("editor.check_mute_duration_warning"),
              positon: "top",
            })
            renderData.editMode = "processfailed"
          } else if (res.code == 6316) {
            message.error({
              content: $$t("create.song_state_auditing_failed"),
              positon: "top",
            })
            renderData.editMode = "processfailed"
          } else {
            message.error({
              content: $$t("editor.get_editing_data_failed_message"),
              positon: "top",
            })
            renderData.editMode = "processfailed"
          }
        }
      })
      .catch((err) => {
        renderData.editMode = "processfailed"
        reject(err)
      })
  })
}

const onRetryClick = () => {
  if (props.item) {
    doParseInitEditDataWithSong()
  } else {
    doParseInitEditData(stateData.parseDataParams)
  }
}
const onCancelClick = () => {
  renderData.editMode = "upload"
  renderData.from = 0
  renderData.to = 0
  stateData.lyricUnitTimeDataArray = []
}

const onSubmitClick = () => {
  globalProperties.logCustomEvent("cover_voice_button_click", {
    tab_stare: "cover_voice",
    content_id: props.item?.song_id,
    source: props.item?.song_id
      ? "mysong"
      : stateData.parseDataParams?.source_type == 1
      ? "youtube_link_upload"
      : "file_upload",
  })
  renderData.loadingSubmit = true
  feedListDataStore
    .doCoverVoiceSongDoneReGenerate(
      {
        parent_song_id: props.item?.song_id,
        type: 1,
        title: renderData.title,
        generation_method: 3,
        is_generated_from_uploaded_file: !props.item?.song_id,
        outer_song: props.item?.song_id
          ? null
          : {
              id: stateData.parseResData?.id,
            },

        artist_id: coverVoiceDataStore.curUseReferMusic?.id,
      },
      renderData.membership_source
    )
    .then(() => {
      renderData.loadingSubmit = false
      renderData.visible = false
    })
    .catch((err) => {
      renderData.loadingSubmit = false
    })
}

const init = async () => {
  if (props.item) {
    doParseInitEditDataWithSong()
  }
}

const poninterEvent = () => {
  if (renderData.visible) {
    if (props.item) {
      globalProperties.logCustomEvent("create_audio_edit_tab_stare_show", {
        tab_stare: "cover_voice",
        content_id: props.item?.song_id,
      })
    } else {
      globalProperties.logCustomEvent("create_audio_edit_tab_stare_show", {
        tab_stare: "cover_voice",
      })
    }
  }
}
watch(
  () => {
    return props.visible
  },
  () => {
    renderData.visible = props.visible
    poninterEvent()
  }
)
watch(
  () => {
    return renderData.visible
  },
  () => {
    emits("update:visible", renderData.visible)
  }
)

onMounted(() => {
  init()
  poninterEvent()
})
</script>
<style lang="scss">
.cover-voice-dialog {
  .cover-voice-dialog-subtitle {
    font-family: HarmonyOS Sans SC;
    font-size: 14px;
    font-weight: normal;
    line-height: 20px;
    color: rgba(255, 255, 255, 0.5);
    margin: 3px 0 0 0;
    .cover-voice-dialog-subtitle-text {
      padding: 0 0 0 15px;
      position: relative;
      &:before {
        content: "";
        display: inline-block;
        width: 2px;
        height: 2px;
        background: rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        position: absolute;
        left: 5px;
        top: 9px;
      }
    }
  }
  &.cover-voice-dialog--mobile {
    .dialog-subtitle {
      display: none;
    }
  }
}
.cover-voice-dialog-container {
  padding: 13px 40px 40px 40px;
  height: 100%;
  display: flex;
  flex-direction: column;
  .cover-voice-dialog-edit-box {
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 0;
    .cover-voice-dialog-media-box {
      border-radius: 16px 16px 0 0;
      background: rgba(148, 173, 255, 0.2);
    }
  }

  .cover-voice-dialog-media-upload {
  }
  .cover-voice-dialog-media-processing {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 196px;
    padding: 20px 0 16px 0;
    .redm-processing-loading-box {
      width: 100px;
      height: 100px;
      position: relative;

      .redm-processing-loading {
        width: 100%;
        height: 100%;
        border-radius: 50%;
        background: conic-gradient(
          from 180deg at 50% 50%,
          rgba(148, 173, 255, 0.28) 0deg,
          rgba(148, 173, 255, 0.8) 54deg,
          rgba(148, 173, 255, 0.2) 95deg,
          rgba(148, 173, 255, 0.1) 136deg,
          rgba(148, 173, 255, 0.28) 178deg,
          rgba(148, 173, 255, 0.8) 236deg,
          rgba(148, 173, 255, 0.2) 279deg,
          rgba(148, 173, 255, 0.1) 321deg,
          rgba(148, 173, 255, 0.28) 360deg,
          rgba(148, 173, 255, 0.8) 414deg
        );
        animation: animation-rotate-infinite 0.75s infinite normal linear;
      }
      .redm-processing-loading-ico {
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        i {
          height: 100%;
          width: 100%;
          background: url(@/assets/img/editor/upload-drag-loading-ico.png);
          background-size: 100% 100%;
        }
      }
    }
    .cover-voice-dialog-media-processing-text {
      font-family: HarmonyOS Sans SC;
      font-size: 14px;
      font-weight: normal;
      line-height: normal;
      color: #ffffff;
      margin: 16px 0 0 0;
    }
  }
  .cover-voice-dialog-media-processfailed {
    padding: 19px 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    .region-processing-processfailed-ico {
      height: 80px;
      width: 80px;
      background: url(@/assets/img/editor/upload-processing-failed-ico.png);
      background-size: 100% 100%;
    }
    .cover-voice-dialog-media-processfailed-text {
      font-family: HarmonyOS Sans SC;
      font-size: 16px;
      font-weight: normal;
      line-height: 22px;
      color: #ffffff;
      margin: 12px 12px;
    }
    .cover-voice-dialog-media-processfailed-button {
      display: flex;
      justify-content: center;
      align-items: center;
      .cover-voice-dialog-media-processfailed-button-item {
        width: 140px;
        &:last-child {
          margin: 0 0 0 10px;
        }
      }
    }
  }
  .cover-voice-dialog-media-edit {
    height: 162px;
  }
  .cover-voice-dialog-lyrics-box {
    margin: 1px 0 0 0;
    height: 0;
    flex: 1;
  }

  .cover-voice-dialog-button-box {
    margin: 20px 0 0 0;
  }
  &.cover-voice-dialog-container--mobile {
    padding: 13px 0 16px 0;

    .cover-voice-dialog-edit-box {
      overflow-y: auto;
      padding: 0 16px;
    }
    .cover-voice-dialog-media-processing {
      height: 160px;
      padding: 22px 0 16px 0;
      .redm-processing-loading-box {
        width: 80px;
        height: 80px;
      }
      .cover-voice-dialog-media-processing-text {
        font-size: 14px;
        line-height: 16px;
        margin: 12px 0 0 0;
      }
    }
    .cover-voice-dialog-media-processfailed {
      padding: 19px 0;
      .region-processing-processfailed-ico {
        height: 64px;
        width: 64px;
      }
      .cover-voice-dialog-media-processfailed-text {
        font-size: 14px;
        line-height: 16px;
        margin: 12px 12px;
      }
    }
    .cover-voice-dialog-media-edit {
      height: 160px;
    }
    .cover-voice-dialog-button-box {
      margin: 16px 16px 0 16px;
    }
  }
}
</style>
